import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, styled } from '@mui/material';
import { CheckCircle2, FileSearch, LucideIcon, Shield, Sparkles } from 'lucide-react';

// ─── Steps config ─────────────────────────────────────────────────────────────

export interface LoadingStep {
  label: string;
  Icon: LucideIcon;
}

const DEFAULT_STEPS: LoadingStep[] = [
  { label: 'Analyzing your zipcode',                   Icon: FileSearch },
  { label: 'Checking plan availability',              Icon: Shield     },
  { label: 'Preparing plan information',  Icon: Sparkles   },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  title?: string;
  subtitle?: string;
  steps?: LoadingStep[];
  onComplete?: () => void;
}

export function PersonalInfoLoading({
  title    = 'Finding plans in your area',
  subtitle = "Reviewing plans in your zipcode",
  steps    = DEFAULT_STEPS,
  onComplete,
}: Props) {
  const [progress,  setProgress]  = useState(0);
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    const pid: any = setInterval(
      () => setProgress(p => (p >= 100 ? (clearInterval(pid), 100) : +(p + 1.2).toFixed(1))),
      80,
    );

    const sid = setInterval(() => {
      setDoneCount(d => {
        if (d >= steps.length) { clearInterval(sid); return d; }
        return d + 1;
      });
    }, 2200);

    const tid = setTimeout(() => onComplete?.(), 2200 * steps.length + 800);

    return () => { clearInterval(pid); clearInterval(sid); clearTimeout(tid); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageWrapper>
      <ContentCard>

        <HeadingBlock>
          <Typography sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' }, fontWeight: 700, lineHeight: 1.2, mb: 1.5 }}>
            {title}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        </HeadingBlock>

        <Box>
          <ProgressTrack sx={{ bgcolor: 'grey.200', mb: 1 }}>
            <ProgressFill value={progress} />
          </ProgressTrack>
          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
            {Math.round(progress) >= 100 ? `Redirecting....` : `${Math.round(progress)}% complete`}
          </Typography>
        </Box>

        <StepList>
          {steps.map(({ label, Icon }, i) => {
            const done   = i < doneCount;
            const active = i === doneCount;

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
              >
                <StepRow active={active} done={done}>
                  <StepIconCircle active={active} done={done}>
                    <motion.span
                      key={done ? 'check' : 'icon'}
                      style={{ display: 'flex' }}
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1,   opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {done ? <CheckCircle2 size={20} /> : <Icon size={20} />}
                    </motion.span>
                  </StepIconCircle>

                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, color: active || done ? 'text.primary' : 'text.secondary' }}
                  >
                    {label}
                  </Typography>
                </StepRow>
              </motion.div>
            );
          })}
        </StepList>

      </ContentCard>
    </PageWrapper>
  );
}


// ─── Styled components ────────────────────────────────────────────────────────

const PageWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  padding: '40px 16px',
  '@media (min-width: 600px)': { padding: '80px 16px' },
});

const ContentCard = styled(Box)({
  width: '100%',
  maxWidth: 560,
  display: 'flex',
  flexDirection: 'column',
  gap: 32,
});

const HeadingBlock = styled(Box)({
  textAlign: 'center',
});

const ProgressTrack = styled(Box)({
  height: 8,
  borderRadius: 4,
  overflow: 'hidden',
});

const ProgressFill = styled(Box)<{ value: number }>(({ value }) => ({
  height: '100%',
  borderRadius: 4,
  backgroundColor: '#094451',
  width: `${value}%`,
  transition: 'width 80ms linear',
}));

const StepList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const StepRow = styled(Box)<{ active: boolean; done: boolean }>(({ active, done }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '14px 16px',
  borderRadius: 10,
  border: '2px solid',
  transition: 'border-color 300ms ease, background-color 300ms ease',
  borderColor: active ? '#094451' : done ? 'rgba(21,128,61,0.3)' : '#e5e7eb',
  backgroundColor: active ? '#E6F4F6' : done ? '#f0fdf4' : '#ffffff',
}));

const StepIconCircle = styled(Box)<{ active: boolean; done: boolean }>(({ active, done }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 300ms ease',
  backgroundColor: active ? '#094451' : done ? '#15803d' : '#e5e7eb',
  color: active || done ? '#ffffff' : '#9ea5b1',
}));
